/**
 * FormFieldRendererSandbox.jsx
 * 
 * Visual-Only Sandbox for testing the refactored FormFieldRenderer
 * 
 * Purpose: Verify 100% visual parity with the live form rendering in App.js
 * before connecting data callbacks.
 * 
 * This sandbox:
 * 1. Uses real form templates from the database
 * 2. Uses the same 12-column grid layout as App.js
 * 3. Supports both Mock Data and Live Data modes
 * 4. Allows side-by-side comparison with live forms
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import FormFieldRenderer from './FormFieldRenderer';
import { createMockContextData } from './FormFieldContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { AlertCircle, CheckCircle2, RefreshCw, Database, FlaskConical, User, Target, Stethoscope } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FormFieldRendererSandbox = () => {
  // State
  const [formTemplates, setFormTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({});
  
  // Live Data Mode state
  const [useLiveData, setUseLiveData] = useState(false);
  const [liveDataLoading, setLiveDataLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDiagnoses, setClientDiagnoses] = useState([]);
  const [clientMedications, setClientMedications] = useState([]);
  const [treatmentPlanGoals, setTreatmentPlanGoals] = useState([]);
  const [linkedGoals, setLinkedGoals] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  
  const [telehealthConsent, setTelehealthConsent] = useState({
    provider_location: '',
    pos_code: '',
    pos_description: '',
    modality: '',
  });

  // Get auth token
  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  // Fetch form templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        
        const token = sessionStorage.getItem('token');
        if (!token) {
          setError('Please log in to access this page');
          setLoading(false);
          return;
        }
        
        const response = await axios.get(`${API}/api/form-templates`, {
          headers: getAuthHeaders()
        });
        const templates = response.data || [];
        setFormTemplates(templates);
        
        // Auto-select first template
        if (templates.length > 0) {
          setSelectedTemplateId(templates[0].id);
          setSelectedTemplate(templates[0]);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching templates:', err);
        setError('Failed to load form templates: ' + (err.response?.data?.detail || err.message));
        setLoading(false);
      }
    };
    
    fetchTemplates();
  }, []);

  // Fetch live data when mode is enabled
  useEffect(() => {
    if (!useLiveData) return;
    
    const fetchLiveData = async () => {
      setLiveDataLoading(true);
      setError(null);
      try {
        const headers = getAuthHeaders();
        
        // Fetch all required data in parallel with individual error handling
        const [
          clientsRes,
          usersRes,
          serviceTypesRes,
          locationsRes,
          organizationsRes,
          meRes
        ] = await Promise.all([
          axios.get(`${API}/api/clients`, { headers }).catch(e => { console.error('Clients fetch failed:', e); return { data: [] }; }),
          axios.get(`${API}/api/users`, { headers }).catch(e => { console.error('Users fetch failed:', e); return { data: [] }; }),
          axios.get(`${API}/api/treatment-planning/service-types`, { headers }).catch(e => { console.error('Service types fetch failed:', e); return { data: [] }; }),
          axios.get(`${API}/api/locations`, { headers }).catch(e => { console.error('Locations fetch failed:', e); return { data: [] }; }),
          axios.get(`${API}/api/organizations`, { headers }).catch(e => { console.error('Organizations fetch failed:', e); return { data: [] }; }),
          axios.get(`${API}/api/auth/me`, { headers }).catch(e => { console.error('Auth/me fetch failed:', e); return { data: null }; }),
        ]);
        
        setClients(clientsRes.data || []);
        setUsers(usersRes.data || []);
        setServiceTypes(serviceTypesRes.data || []);
        setLocations(locationsRes.data || []);
        setOrganizations(organizationsRes.data || []);
        setCurrentUser(meRes.data || null);
        
        void 0 && console.log('Live data loaded:', {
          clients: clientsRes.data?.length || 0,
          users: usersRes.data?.length || 0,
          serviceTypes: serviceTypesRes.data?.length || 0,
          locations: locationsRes.data?.length || 0,
          organizations: organizationsRes.data?.length || 0,
        });
        
      } catch (err) {
        console.error('Error fetching live data:', err);
        setError('Failed to load live data: ' + (err.response?.data?.detail || err.message));
      } finally {
        setLiveDataLoading(false);
      }
    };
    
    fetchLiveData();
  }, [useLiveData]);

  // Fetch client-specific data when client is selected
  const fetchClientData = useCallback(async (clientId) => {
    if (!clientId) return;
    
    try {
      const headers = getAuthHeaders();
      
      // Fetch diagnoses, treatment plan goals, and medications for this client
      const [diagnosesRes, goalsRes, medicationsRes] = await Promise.all([
        axios.get(`${API}/api/clients/${clientId}/diagnoses`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/clients/${clientId}/treatment-plan-goals`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/clients/${clientId}/medications`, { headers }).catch(() => ({ data: [] })),
      ]);
      
      setClientDiagnoses(diagnosesRes.data || []);
      setTreatmentPlanGoals(goalsRes.data || []);
      setClientMedications(medicationsRes.data || []);
      
      // Find the selected client object
      const client = clients.find(c => c.id === clientId);
      setSelectedClient(client || null);
      
    } catch (err) {
      console.error('Error fetching client data:', err);
    }
  }, [clients]);

  // Fetch client data when selection changes
  useEffect(() => {
    if (useLiveData && selectedClientId) {
      fetchClientData(selectedClientId);
    }
  }, [useLiveData, selectedClientId, fetchClientData]);

  // Update selected template when dropdown changes
  useEffect(() => {
    if (selectedTemplateId && formTemplates.length > 0) {
      const template = formTemplates.find(t => t.id === selectedTemplateId);
      setSelectedTemplate(template);
      setFormData({}); // Reset form data when template changes
      setLinkedGoals([]); // Reset linked goals
    }
  }, [selectedTemplateId, formTemplates]);

  // Handle form data changes
  const handleFormChange = (updater) => {
    if (typeof updater === 'function') {
      setFormData(updater);
    } else {
      setFormData(prev => ({ ...prev, ...updater }));
    }
  };

  // Build context based on mode (mock vs live)
  const buildContext = () => {
    if (useLiveData) {
      return {
        // Client data
        selectedClient: selectedClient,
        clients: clients,
        selectedNoteClients: selectedClientId ? [selectedClientId] : [],
        clientDiagnoses: clientDiagnoses,
        clientMedications: clientMedications,
        
        // User data
        user: currentUser,
        users: users,
        
        // Appointments (empty for sandbox)
        appointments: [],
        
        // Form data
        newNoteFormData: formData,
        formTemplates: formTemplates,
        
        // Treatment Plan data
        linkedGoals: linkedGoals,
        treatmentPlanGoals: treatmentPlanGoals,
        
        // Telehealth/Consent
        telehealthConsent: telehealthConsent,
        setTelehealthConsent: setTelehealthConsent,
        organizations: organizations,
        posCodes: organizations[0]?.pos_codes || [],
        
        // Dropdown data
        serviceTypes: serviceTypes,
        locations: locations,
        providers: users.filter(u => u.is_provider),
        allGroups: [],
        
        // Group note data
        isGroupNote: false,
        groupMembers: [],
        groupAttendance: {},
        groupMemberNotes: {},
        
        // Note state
        selectedNote: null,
        isEditMode: false,
        disabled: false,
        
        // Callbacks
        setNewNoteFormData: handleFormChange,
        setSelectedClient: setSelectedClient,
        setShowDiagnosisForm: () => void 0 && console.log('Would show diagnosis form'),
        setShowLinkGoalsDialog: () => void 0 && console.log('Would show link goals dialog'),
        fetchTreatmentPlanGoals: fetchClientData,
        setLinkedGoals: setLinkedGoals,
        setGroupAttendance: () => {},
        setGroupMemberNotes: () => {},
      };
    } else {
      // Mock data mode
      const baseMockData = createMockContextData();
      return {
        ...baseMockData,
        newNoteFormData: formData,
        formTemplates: formTemplates,
        telehealthConsent: telehealthConsent,
        setTelehealthConsent: setTelehealthConsent,
        setLinkedGoals: setLinkedGoals,
        linkedGoals: linkedGoals.length > 0 ? linkedGoals : baseMockData.linkedGoals,
      };
    }
  };

  // Get fields from template
  const getTemplateFields = () => {
    if (!selectedTemplate) return [];
    return selectedTemplate.form_fields || selectedTemplate.fields || [];
  };

  // Get sections from template
  const getTemplateSections = () => {
    if (!selectedTemplate) return null;
    return selectedTemplate.sections || null;
  };

  // Count field types
  const getFieldTypeStats = () => {
    const fields = getTemplateFields();
    const stats = {};
    fields.forEach(f => {
      const type = f.type || 'unknown';
      stats[type] = (stats[type] || 0) + 1;
    });
    return stats;
  };

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <Card>
          <CardContent className="p-8 text-center">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p>Loading form templates...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <Card>
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-8 w-8 mx-auto mb-4 text-red-600" />
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const templateFields = getTemplateFields();
  const templateSections = getTemplateSections();
  const fieldStats = getFieldTypeStats();
  const context = buildContext();

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
                FormFieldRenderer V2 - Sandbox
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                Test form rendering with mock or live production data
              </p>
            </div>
            
            {/* Data Mode Toggle */}
            <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <FlaskConical className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">Mock</span>
              </div>
              <Switch
                checked={useLiveData}
                onCheckedChange={setUseLiveData}
                disabled={liveDataLoading}
              />
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-600">Live Data</span>
              </div>
              {liveDataLoading && <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Template Selection */}
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Form Template</Label>
              <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a form template..." />
                </SelectTrigger>
                <SelectContent>
                  {formTemplates.map(template => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Client Selection (Live Data Mode Only) */}
            {useLiveData && (
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Select Client (for diagnoses & goals)</Label>
                <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.slice(0, 50).map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          
          {/* Live Data Status */}
          {useLiveData && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="flex items-center gap-2 p-2 bg-blue-50 rounded">
                <User className="h-4 w-4 text-blue-600" />
                <span className="text-xs">{clients.length} Clients</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-purple-50 rounded">
                <Stethoscope className="h-4 w-4 text-purple-600" />
                <span className="text-xs">{clientDiagnoses.length} Diagnoses</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-green-50 rounded">
                <Target className="h-4 w-4 text-green-600" />
                <span className="text-xs">{treatmentPlanGoals.length} Goals Available</span>
              </div>
              <div className="flex items-center gap-2 p-2 bg-orange-50 rounded">
                <Database className="h-4 w-4 text-orange-600" />
                <span className="text-xs">{serviceTypes.length} Services</span>
              </div>
            </div>
          )}
          
          {/* Field Type Breakdown */}
          <div className="flex flex-wrap gap-2">
            {Object.entries(fieldStats).map(([type, count]) => (
              <Badge key={type} variant="outline" className="text-xs">
                {type}: {count}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Form Rendering Area */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Form Preview</CardTitle>
              <p className="text-sm text-gray-500">
                {useLiveData ? 'Rendering with LIVE production data' : 'Rendering with mock data'}
              </p>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                setFormData({});
                setLinkedGoals([]);
              }}
            >
              Reset Form
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg p-4 bg-white min-h-[200px]">
            {templateFields.length === 0 && !templateSections ? (
              <p className="text-gray-500 italic text-center py-8">
                No fields in this template. Select a different template.
              </p>
            ) : (
              <FormFieldRenderer
                fields={templateFields}
                sections={templateSections}
                contextValue={context}
                onChange={handleFormChange}
                disabled={false}
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Debug Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Debug Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <details>
              <summary className="cursor-pointer font-medium text-sm text-gray-600">
                Form Data ({Object.keys(formData).length} fields)
              </summary>
              <pre className="mt-2 p-4 border rounded bg-gray-100 text-xs overflow-auto max-h-48">
                {JSON.stringify(formData, null, 2)}
              </pre>
            </details>
            
            <details>
              <summary className="cursor-pointer font-medium text-sm text-gray-600">
                Linked Goals ({linkedGoals.length})
              </summary>
              <pre className="mt-2 p-4 border rounded bg-gray-100 text-xs overflow-auto max-h-48">
                {JSON.stringify(linkedGoals, null, 2)}
              </pre>
            </details>
            
            {useLiveData && (
              <>
                <details>
                  <summary className="cursor-pointer font-medium text-sm text-gray-600">
                    Client Diagnoses ({clientDiagnoses.length})
                  </summary>
                  <pre className="mt-2 p-4 border rounded bg-gray-100 text-xs overflow-auto max-h-48">
                    {JSON.stringify(clientDiagnoses, null, 2)}
                  </pre>
                </details>
                
                <details>
                  <summary className="cursor-pointer font-medium text-sm text-gray-600">
                    Treatment Plan Goals ({treatmentPlanGoals.length})
                  </summary>
                  <pre className="mt-2 p-4 border rounded bg-gray-100 text-xs overflow-auto max-h-48">
                    {JSON.stringify(treatmentPlanGoals, null, 2)}
                  </pre>
                </details>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FormFieldRendererSandbox;
